import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Box, Grid, Button, FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField, Typography, Paper } from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import OrderDetailsModal from 'Components/OrderDetailsModal';
import CopyOrdersToNextYearModal from 'Components/CopyOrdersToNextYearModal';
import { loadBillingFilters, saveBillingFilters, clearBillingCompanyFilter } from 'Includes/billingFilterStorage';

function CompanyOrderList(props) {
  const storedFilters = loadBillingFilters();
  const [rows, setRows] = React.useState([])
  const [orderSummary, setOrderSummary] = React.useState({ total_without_tax: 0, tax_amount: 0, total_amount: 0 })
  const [rowCount, setRowCount] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [academicYearList, setAcademicYearList] = React.useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '')
  const [companies, setCompanies] = React.useState([])
  const [selectedCompany, setSelectedCompany] = React.useState(null)
  const [serviceList, setServiceList] = React.useState([])
  const [selectedService, setSelectedService] = React.useState(null)
  const [inactiveDeadClient, setInactiveDeadClient] = React.useState(false) // Default: false (active clients only)
  const [detailOpen, setDetailOpen] = React.useState(false)
  const [detailLoading, setDetailLoading] = React.useState(false)
  const [detailData, setDetailData] = React.useState(null)
  const [selectedRows, setSelectedRows] = React.useState([])
  const [copyModalOpen, setCopyModalOpen] = React.useState(false)
  const [copyModalLoading, setCopyModalLoading] = React.useState(false)
  const [copyFormData, setCopyFormData] = React.useState({})
  const isSuperuser = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}')
      return Boolean(u?.is_superuser)
    } catch {
      return false
    }
  }, [])

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    { field: 'billing_config', headerName: 'billing_config', width: 90, hide: true },
    {
      field: 'company_name',
      headerName: 'Company name',
      width: 150,
    },
    {
      field: 'service_name',
      headerName: 'Service',
      width: 150,
    },
    {
      field: 'order_date',
      headerName: 'Ordered On',
      width: 150,
    },

    {
      field: 'service_duration',
      headerName: 'Svc Duration (Years)',
      width: 150,
    },

    {
      field: 'service_end_date',
      headerName: 'Svc Ends On',
      width: 150,
    },

    {
      field: 'item_count',
      headerName: 'Item Count',
      width: 150,
    },
    {
      field: 'service_amount',
      headerName: 'Order Amount',
      width: 150,
    },

    {
      field: 'service_gst_percentage',
      headerName: 'GST Amount',
      width: 150,
    },
    {
      field: 'total_without_tax',
      headerName: 'Total Excl Tax',
      width: 150,
    },
    {
      field: 'tax_amount',
      headerName: 'Tax Amount',
      width: 150,
    },
    {
      field: 'total_amount',
      headerName: 'Total Amount',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem label="View Details" onClick={() => handleViewDetails(params?.row)} showInMenu />,
      ]
    }
  ];

  React.useEffect(() => {
    getAcademicYearList()
    getServiceList()
  }, [])

  const getAcademicYearList = () => {
    const url = GET_URL.academicyear.api;
    const params = {};
    getRequest(url, params, props).then((response) => {
      if (response && response.status === 200) {
        setAcademicYearList(response.data.data);
      }
    });
  };

  const getServiceList = () => {
    const url = GET_URL.services?.api;
    const params = {};
    getRequest(url, params, props).then((response) => {
      if (response && response.status === 200) {
        setServiceList(response.data.data || []);
      }
    }).catch(() => {});
  };

  const handleAcademicYearChange = (event) => {
    const yearId = event.target.value;
    setSelectedAcademicYear(yearId);
    clearBillingCompanyFilter()
    setSelectedCompany(null)
    setSelectedService(null)
    setOrderSummary({ total_without_tax: 0, tax_amount: 0, total_amount: 0 })
    if (yearId) {
      setPage(0)
    } else {
      setRows([]);
    }
  };

  const handleInactiveDeadClientChange = (event) => {
    const value = event.target.value === 'dead';
    setInactiveDeadClient(value);
    setSelectedRows([]);
    if (selectedAcademicYear) {
      setPage(0);
      getCompanyOrderList(selectedAcademicYear, selectedCompany?.company || null, selectedService?.id || null, 0, pageSize, value);
    }
  };

  const getCompanyOrderList = (academicYearId, compId, serviceId = null, p = page, ps = pageSize, includeDead = inactiveDeadClient) => {
    const url = GET_URL.service_order.api
    const params = { year: academicYearId, company: compId, limit: ps, pageno: (p + 1) }
    if (serviceId) {
      params.service = serviceId
    }
    // Add inactive_dead_client filter (mandatory)
    if (includeDead) {
      params.include_dead_client_only = 1
    }
    getRequest(url, params, props).then(response => {
      if (response && response.status === 200) {
        const list = response.data?.data || []
        const count = response.data?.count || (Array.isArray(list) ? list.length : 0)
        setRows(list)
        setRowCount(count)
        const sum = response.data?.summary || {}
        setOrderSummary({
          total_without_tax: parseFloat(sum?.total_without_tax || 0) || 0,
          tax_amount: parseFloat(sum?.tax_amount || 0) || 0,
          total_amount: parseFloat(sum?.total_amount || 0) || 0,
        })
      }
    })
  }

  const handleViewDetails = (row) => {
    if (!row?.id) return
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailData(null)
    getRequest(`${GET_URL.service_order.api}${row.id}/`, { view_details: 1 }, props)
      .then((response) => {
        const payload = response?.data?.data || response?.data
        setDetailData(payload || null)
      })
      .finally(() => setDetailLoading(false))
  }

  const handleCloseDetails = () => {
    setDetailOpen(false)
    setDetailLoading(false)
    setDetailData(null)
  }

  const getCompanies = (year) => {
    const url = GET_URL.company_billing_config.api
    getRequest(url, { year }, props)
      .then(response => {
        if (response && response.status === 200) {
          const data = response.data.data || response.data || []
          // Deduplicate by company — multiple billing configs per company otherwise repeat names.
          const optionsMap = new Map()
          ;(Array.isArray(data) ? data : []).forEach((row) => {
            if (row?.company != null && !optionsMap.has(row.company)) {
              optionsMap.set(row.company, row)
            }
          })
          setCompanies(Array.from(optionsMap.values()))
          const uniqueCompanies = Array.from(optionsMap.values())
          const sf = loadBillingFilters()
          let companyId = sf.companyId
          if (!companyId && sf.companyConfigId) {
            const raw = data.find((c) => String(c.id) === String(sf.companyConfigId))
            companyId = raw?.company
          }
          const match = companyId
            ? uniqueCompanies.find((c) => String(c.company) === String(companyId))
            : null
          if (match) {
            setSelectedCompany(match)
            setPage(0)
            getCompanyOrderList(selectedAcademicYear, match.company, selectedService?.id || null, 0, pageSize, inactiveDeadClient)
          } else {
            setPage(0)
            getCompanyOrderList(selectedAcademicYear, null, selectedService?.id || null, 0, pageSize, inactiveDeadClient)

          }
        }
      })
  }

  const handleCompanyChange = (_e, value) => {
    setSelectedCompany(value)
    if (selectedAcademicYear) {
      saveBillingFilters({
        academicYearId: selectedAcademicYear,
        companyId: value?.company,
        companyConfigId: value?.id,
        companyName: value?.company_name,
      })
      setPage(0)
      getCompanyOrderList(selectedAcademicYear, value?.company, selectedService?.id || null, 0, pageSize, inactiveDeadClient)
    }
  }

  const handleServiceChange = (_e, value) => {
    setSelectedService(value)
    if (selectedAcademicYear) {
      setPage(0)
      getCompanyOrderList(selectedAcademicYear, selectedCompany?.company || null, value?.id || null, 0, pageSize, inactiveDeadClient)
    }
  }

  React.useEffect(() => {
    if (selectedAcademicYear) {
      saveBillingFilters({ academicYearId: selectedAcademicYear })
      getCompanies(selectedAcademicYear)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAcademicYear])

  React.useEffect(() => {
    if (selectedAcademicYear) {
      getCompanyOrderList(selectedAcademicYear, selectedCompany?.company || null, selectedService?.id || null, page, pageSize, inactiveDeadClient)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactiveDeadClient])


  const handlePageChange = (newPage) => {
    setPage(newPage)
    if (selectedAcademicYear) {
      getCompanyOrderList(selectedAcademicYear, selectedCompany?.company || null, selectedService?.id || null, newPage, pageSize, inactiveDeadClient)
    }
  }

  const handlePageSizeChange = (newPageSize) => {
    const resetPage = 0
    setPageSize(newPageSize)
    setPage(resetPage)
    if (selectedAcademicYear) {
      getCompanyOrderList(selectedAcademicYear, selectedCompany?.company || null, selectedService?.id || null, resetPage, newPageSize, inactiveDeadClient)
    }
  }

  const handleOpenCopyModal = async () => {
    if (selectedRows.length === 0) return
    setCopyModalOpen(true)
    // Create deep copy of selected rows to avoid mutating the original rows array
    const selectedRowsData = rows
      .filter(r => selectedRows.includes(r.id))
      .map(row => {
        // Deep clone the row object
        const cloned = JSON.parse(JSON.stringify(row))
        // Store original dates for later calculation
        cloned.original_service_end_date = cloned.service_end_date
        cloned.original_order_date = cloned.order_date
        // Clear dates - will be set to defaults in modal
        cloned.service_end_date = null
        cloned.order_date = null
        if (cloned.is_base_amount_to_be_reset_after_year) {
          cloned.service_amount = ''
        }
        if (cloned.serviceplan_data?.service_type === 'STUDENT_WISE') {
          cloned.item_count = cloned?.billing_config_data?.student_strength || cloned.item_count
        }
        // Deep clone payment terms and store original dates
        if (cloned.payment_terms) {
          cloned.payment_terms = cloned.payment_terms.map(term => {
            const clonedTerm = { ...term }
            clonedTerm.original_term_receive_date = clonedTerm.term_receive_date
            clonedTerm.term_receive_date = null
            return clonedTerm
          })
        }
        return cloned
      })
    setCopyModalLoading(false)

    setCopyFormData({
      pricing_services: selectedRowsData
    })
  }

  const handleCloseCopyModal = () => {
    setCopyModalOpen(false)
    setCopyFormData({})
  }

  const handleCopySuccess = () => {
    setSelectedRows([])
    // Refresh the list
    if (selectedAcademicYear) {
      getCompanyOrderList(selectedAcademicYear, selectedCompany?.company || null, selectedService?.id || null, page, pageSize, inactiveDeadClient)
    }
  }

  // Get available years (excluding current selected year)
  const availableYears = academicYearList.filter(year => String(year.id) !== String(selectedAcademicYear))

  return (
    <Box sx={{ p: 2 }}>
  
      {/* HEADER */}
        <Grid container alignItems="center">
  
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight="bold">
              Copy Orders to Next Year
            </Typography>
          </Grid>
  
          <Grid item xs={12} md={6} textAlign="right">
            {selectedRows.length > 0 && (
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineOutlined />}
                onClick={handleOpenCopyModal}
              >
                Copy ({selectedRows.length})
              </Button>
            )}
          </Grid>
  
        </Grid>
  
      {/* FILTERS */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
  
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={handleAcademicYearChange}
                label="Academic Year"
              >
                {academicYearList.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
  
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={companies}
              getOptionLabel={(o) => o.company_name || ''}
              isOptionEqualToValue={(option, value) => String(option?.company) === String(value?.company)}
              value={selectedCompany}
              onChange={handleCompanyChange}
              renderInput={(params) => (
                <TextField {...params} label="Company" size="small" />
              )}
            />
          </Grid>
  
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={serviceList}
              getOptionLabel={(o) => o.name || ''}
              value={selectedService}
              onChange={handleServiceChange}
              renderInput={(params) => (
                <TextField {...params} label="Service" size="small" />
              )}
            />
          </Grid>
  
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={inactiveDeadClient ? 'dead' : 'active'}
                onChange={handleInactiveDeadClientChange}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="dead">Inactive / Dead</MenuItem>
              </Select>
            </FormControl>
          </Grid>
  
        </Grid>
      </Paper>
  
      {/* SUMMARY */}
      {selectedAcademicYear && orderSummary?.total_without_tax && isSuperuser && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#f5f7fb' }}>
          <Typography fontWeight="bold" mb={1}>
            Overall Summary
          </Typography>
  
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption">
                Excl Tax: {formatAmount(orderSummary.total_without_tax)}
              </Typography>
            </Grid>
  
            <Grid item xs={12} md={4}>
              <Typography variant="caption">
                Tax: {formatAmount(orderSummary.tax_amount)}
              </Typography>
            </Grid>
  
            <Grid item xs={12} md={4}>
              <Typography fontWeight="bold">
                Total: {formatAmount(orderSummary.total_amount)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
  
      {/* TABLE */}
      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== pageSize) {
              handlePageSizeChange(model.pageSize)
            } else if (model.page !== page) {
              handlePageChange(model.page)
            }
          }}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          checkboxSelection
          selectionModel={selectedRows}
          onSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          autoHeight
          rowHeight={36}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f7fb',
              fontWeight: 'bold'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9fafc'
            }
          }}
        />
      </Paper>
  
      {/* MODALS */}
      <OrderDetailsModal
        open={detailOpen}
        loading={detailLoading}
        data={detailData}
        onClose={handleCloseDetails}
      />
  
      <CopyOrdersToNextYearModal
        open={copyModalOpen}
        onClose={handleCloseCopyModal}
        loading={copyModalLoading}
        initialFormData={copyFormData}
        availableYears={availableYears}
        onSuccess={handleCopySuccess}
        props={props}
      />
  
    </Box>
  );

}

export default CompanyOrderList;
